/*eslint-disable*/
// Builder.tsx
import React, { useMemo, createContext, useState } from 'react';
import { Editor, Frame, useEditor, type SerializedNodes } from '@craftjs/core';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles/builderStyles.module.css';

import SnapCanvas from '../SnapCanvas/SnapCanvas';
import { BuilderToolbox } from '../BuilderToolbox/BuilderToolbox';
import InputField from '../BuilderInputField/InputField';
import CheckboxField from '../Checkbox/CheckboxField';
import MultilineTextbox from '../MultilineTextbox/MultilineTextbox';
import UploadArea from '../UploadArea/UploadArea';
import DropdownField from '../DropdownField/DropdownField';
import DatePickerField from '../DatePicker/DatePicker';
import TimePickerField from '../TimePickerField/TimePickerField';
import { FaArrowLeftLong } from 'react-icons/fa6';
import {
  selectFormLayout,
  selectOutputLayout,
  setFormLayout,
  setOutputLayout,
} from '../../Redux/slices/builderLayoutSlice';
import { selectFormView } from '../../Redux/slices/formToggleSlice';
import { InteractionModeProvider } from '../contexts/InteractionMode';
import ExistingLayouts from './ExistingLayouts';

// ===== Types / Context =====
export type FormView = 'FORM' | 'REPORT';
export const BuilderModeContext = createContext<FormView>('FORM');

// ===== Helpers =====
const hasRoot = (json: string | null | undefined) =>
  !!json && json !== '{}' && json.includes('"ROOT"');

const makeEmptyTree = (): SerializedNodes => ({
  ROOT: {
    type: { resolvedName: 'SnapCanvas' },
    isCanvas: true,
    props: { showGrid: true, id: 'snap_canvas' },
    displayName: 'SnapCanvas',
    custom: {},
    parent: null,
    hidden: false,
    nodes: [],
    linkedNodes: {},
  },
});

// === Field sync config ===
const FIELD_COMPONENTS = new Set([
  'InputField',
  'CheckboxField',
  'MultilineTextbox',
  'UploadArea',
  'DropdownField',
  'DatePickerField',
  'TimePickerField',
]);

const PROP_WHITELIST = ['label', 'placeholder'] as const;
const POSITION_KEYS = ['left', 'top', 'width', 'height'] as const;

// === JSON utilities ===
const safeParse = (
  s: string | SerializedNodes | null
): SerializedNodes | null => {
  if (!s) return null;
  if (typeof s === 'string') {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }
  return s;
};
const safeStringify = (obj: any): string => {
  try {
    return JSON.stringify(obj);
  } catch {
    return '{}';
  }
};
const deepClone = (o: any) => JSON.parse(JSON.stringify(o));
const parseMaybe = (
  s: string | SerializedNodes | null
): SerializedNodes | null => safeParse(s);

const treeHasChildren = (tree: SerializedNodes | null): boolean => {
  if (!tree) return false;
  const root = (tree as any).ROOT;
  return !!root && Array.isArray(root.nodes) && root.nodes.length > 0;
};
const isEmptyTree = (s: string | SerializedNodes | null) =>
  !treeHasChildren(parseMaybe(s));

// Assign a stable bindingId to fields (once). Use node key as default.
const ensureBindingIds = (
  tree: SerializedNodes | null
): SerializedNodes | null => {
  if (!tree) return tree;
  const t = deepClone(tree);
  for (const nodeId of Object.keys(t)) {
    const n = (t as any)[nodeId];
    const name = n?.type?.resolvedName as string | undefined;
    if (name && FIELD_COMPONENTS.has(name)) {
      if (!n.props) n.props = {};
      if (!n.props.bindingId) n.props.bindingId = nodeId;
    }
  }
  return t;
};

// Unique node id inside a serialized tree
const genNodeId = (tree: any, base: string) => {
  let candidate = base;
  let i = 0;
  while (tree[candidate]) {
    i += 1;
    candidate = `${base}_${i}`;
  }
  return candidate;
};

// Minimal Craft node for field components
const makeFieldNode = ({
  resolvedName,
  parent = 'ROOT',
  props = {},
}: {
  resolvedName: string;
  parent?: string;
  props: Record<string, any>;
}) => ({
  type: { resolvedName },
  isCanvas: false,
  props,
  displayName: resolvedName,
  custom: {},
  parent,
  hidden: false,
  nodes: [],
  linkedNodes: {},
});

// Copy whitelisted props from source → target by bindingId.
// If the target lacks a counterpart, create it once and copy initial position.
// After a counterpart exists, we NEVER overwrite POSITION_KEYS again.
const ensureMirrorsAndSyncProps = (
  src: SerializedNodes | null,
  tgt: SerializedNodes | null
): SerializedNodes | null => {
  if (!src || !tgt) return tgt;
  const out = deepClone(tgt);

  const srcRoot = (src as any).ROOT;
  const outRoot = (out as any).ROOT;
  if (!srcRoot || !outRoot) return out;

  // Build quick index of target nodes by bindingId
  const targetByBinding: Record<string, string> = {};
  for (const nodeId of Object.keys(out)) {
    const n = (out as any)[nodeId];
    if (!n || !n.type) continue;
    const name = n.type.resolvedName as string | undefined;
    if (!name || !FIELD_COMPONENTS.has(name)) continue;
    const bid = n.props?.bindingId;
    if (bid) targetByBinding[bid] = nodeId;
  }

  // Walk source nodes
  for (const srcNodeId of Object.keys(src)) {
    const s = (src as any)[srcNodeId];
    if (!s || !s.type) continue;
    const name = s.type.resolvedName as string | undefined;
    if (!name || !FIELD_COMPONENTS.has(name)) continue;

    const bid = s.props?.bindingId ?? srcNodeId; // ensureBindingIds should have set this
    const srcProps = s.props ?? {};

    // Does target already have this binding?
    const existingTargetId = targetByBinding[bid];

    if (!existingTargetId) {
      // Create a new mirror node in the target
      const nodeId = genNodeId(out, bid); // stable & unique in this tree

      // Start with whitelisted props + bindingId
      const initialProps: Record<string, any> = { bindingId: bid };
      for (const k of PROP_WHITELIST) {
        if (k in srcProps) (initialProps as any)[k] = srcProps[k];
      }
      // Copy initial position ONLY on creation (never overwritten later)
      for (const k of POSITION_KEYS) {
        if (k in srcProps) (initialProps as any)[k] = srcProps[k];
      }

      (out as any)[nodeId] = makeFieldNode({
        resolvedName: name,
        parent: 'ROOT',
        props: initialProps,
      });

      // Append to ROOT children
      if (!Array.isArray(outRoot.nodes)) outRoot.nodes = [];
      outRoot.nodes = [...outRoot.nodes, nodeId];

      // Remember we now have this binding in the target
      targetByBinding[bid] = nodeId;
      continue;
    }

    // Counterpart exists → update ONLY whitelisted props (never positions)
    const t = (out as any)[existingTargetId];
    if (!t.props) t.props = {};
    for (const k of PROP_WHITELIST) {
      if (k in srcProps) t.props[k] = srcProps[k];
    }
    // DO NOT touch POSITION_KEYS here — keeps positions decoupled
  }

  return out;
};

/** **********************************************************************
 * CraftSync
 * - Persists live edits to Redux & in-memory snapshots
 * - Creates mirror nodes in the opposite branch on first drop
 * - Syncs label/placeholder both ways; never overwrites positions post-creation
 * - Skips saving on the first frame after deserialize
 * - Bootstraps FORM/REPORT when missing
 *************************************************************************/
const CraftSync: React.FC<{
  initialJson: string | SerializedNodes | null; // frozen snapshot for the *selected* branch
  selected: 'FORM' | 'REPORT';

  // authoritative snapshot getters/setters (owned by Builder)
  getFormSnapshot: () => string | SerializedNodes | null;
  setFormSnapshot: (s: string | SerializedNodes) => void;
  getReportSnapshot: () => string | SerializedNodes | null;
  setReportSnapshot: (s: string | SerializedNodes) => void;

  // top-level Redux copies (for last-resort seeding)
  formLayoutRedux: string | null;
  outputLayoutRedux: string | null;

  // flags only used for initial bootstrap into Redux when empty
  shouldBootstrapForm: boolean;
  shouldBootstrapOutput: boolean;
}> = ({
  initialJson,
  selected,
  getFormSnapshot,
  setFormSnapshot,
  getReportSnapshot,
  setReportSnapshot,
  formLayoutRedux,
  outputLayoutRedux,
  shouldBootstrapForm,
  shouldBootstrapOutput,
}) => {
  const dispatch = useDispatch();
  const { actions, query } = useEditor();

  // Which branch is currently loaded in the editor
  const loadedBranchRef = React.useRef<'FORM' | 'REPORT'>(selected);

  // True during/just-after deserialize; prevents immediate save loops
  const switchingRef = React.useRef<boolean>(true);

  // The snapshot we last loaded (to skip the first save frame)
  const lastLoadedSerializedRef = React.useRef<string | null>(null);

  // Live serialization stream from Craft
  const { serialized } = useEditor((_, q): { serialized: string } => ({
    serialized: q.serialize(),
  }));

  // ====== Branch Switch Handler (runs when `selected` changes) ======
  React.useEffect(() => {
    // 1) Persist the *previous* branch synchronously from Craft, not Redux
    const prevBranch = loadedBranchRef.current;
    const liveNow = query.serialize(); // exact live editor state *before* switching
    if (liveNow && liveNow.includes('"ROOT"')) {
      const withIdsObj = ensureBindingIds(safeParse(liveNow));
      const normalizedLive = safeStringify(withIdsObj);

      if (prevBranch === 'FORM') {
        // Cross-pollinate props into REPORT snapshot before we leave FORM
        const reportSnap = ensureBindingIds(parseMaybe(getReportSnapshot()));
        const formSnapObj = ensureBindingIds(parseMaybe(normalizedLive));
        const updatedReportSnap = ensureMirrorsAndSyncProps(
          formSnapObj,
          reportSnap
        );
        if (updatedReportSnap) {
          const s = safeStringify(updatedReportSnap);
          setReportSnapshot(s);
          dispatch(setOutputLayout(s));
        }
        setFormSnapshot(normalizedLive);
        dispatch(setFormLayout(normalizedLive));
      } else {
        // Leaving REPORT → pollinate into FORM
        const formSnap = ensureBindingIds(parseMaybe(getFormSnapshot()));
        const reportSnapObj = ensureBindingIds(parseMaybe(normalizedLive));
        const updatedFormSnap = ensureMirrorsAndSyncProps(
          reportSnapObj,
          formSnap
        );
        if (updatedFormSnap) {
          const s = safeStringify(updatedFormSnap);
          setFormSnapshot(s);
          dispatch(setFormLayout(s));
        }
        setReportSnapshot(normalizedLive);
        dispatch(setOutputLayout(normalizedLive));
      }
    }

    // 2) If entering REPORT and it is empty, auto-clone from best available source
    let nextLayout: string | SerializedNodes | null = initialJson;

    if (selected === 'REPORT') {
      const currentReportSnap = ensureBindingIds(
        parseMaybe(getReportSnapshot())
      );
      const reportHasKids = treeHasChildren(currentReportSnap);

      if (!reportHasKids) {
        // Preferred source #1: liveNow we just captured (if it has children)
        let sourceTree: SerializedNodes | null = null;
        const liveTree = ensureBindingIds(parseMaybe(liveNow || null));
        if (treeHasChildren(liveTree)) {
          sourceTree = liveTree;
        } else {
          // Preferred source #2: in-memory FORM snapshot
          const formMem = ensureBindingIds(parseMaybe(getFormSnapshot()));
          if (treeHasChildren(formMem)) {
            sourceTree = formMem;
          } else {
            // Preferred source #3: Redux FORM layout
            const formRedux = ensureBindingIds(parseMaybe(formLayoutRedux));
            if (treeHasChildren(formRedux)) {
              sourceTree = formRedux!;
            }
          }
        }

        const cloned = sourceTree ? deepClone(sourceTree) : makeEmptyTree();
        const clonedStr = safeStringify(cloned);

        setReportSnapshot(clonedStr);
        dispatch(setOutputLayout(clonedStr));

        // show this immediately
        nextLayout = clonedStr;
      }
    }

    // 3) Switch to the new branch by deserializing the chosen layout
    switchingRef.current = true;

    if (nextLayout) {
      if (typeof nextLayout === 'string') {
        if (hasRoot(nextLayout)) {
          actions.deserialize(nextLayout);
          lastLoadedSerializedRef.current = nextLayout;
        } else {
          lastLoadedSerializedRef.current = null;
        }
      } else {
        actions.deserialize(nextLayout as SerializedNodes);
        try {
          lastLoadedSerializedRef.current = JSON.stringify(nextLayout);
        } catch {
          lastLoadedSerializedRef.current = null;
        }
      }
    } else {
      lastLoadedSerializedRef.current = null;
    }

    loadedBranchRef.current = selected;
    requestAnimationFrame(() => {
      switchingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Skip treating the first post-deserialize frame as an edit
  React.useEffect(() => {
    if (!serialized || !serialized.includes('"ROOT"')) return;
    if (switchingRef.current) return;

    if (
      lastLoadedSerializedRef.current &&
      serialized === lastLoadedSerializedRef.current
    ) {
      lastLoadedSerializedRef.current = null; // consume the skip
      return;
    }
  }, [serialized]);

  // Bootstrap: write initial snapshot to Redux only if the branch had none
  React.useEffect(() => {
    if (!serialized?.includes('"ROOT"')) return;
    if (switchingRef.current) return;
    if (loadedBranchRef.current !== selected) return;

    const normalizedObj = ensureBindingIds(safeParse(serialized));
    const normalizedStr = safeStringify(normalizedObj);

    if (shouldBootstrapForm && selected === 'FORM') {
      // Pollinate into REPORT snapshot on first bootstrap
      const reportSnap = ensureBindingIds(parseMaybe(getReportSnapshot()));
      const updatedReportSnap = ensureMirrorsAndSyncProps(
        normalizedObj,
        reportSnap
      );
      if (updatedReportSnap) {
        const s = safeStringify(updatedReportSnap);
        setReportSnapshot(s);
        dispatch(setOutputLayout(s));
      }

      dispatch(setFormLayout(normalizedStr));
      setFormSnapshot(normalizedStr);
      lastLoadedSerializedRef.current = normalizedStr;
    }
    if (shouldBootstrapOutput && selected === 'REPORT') {
      // Pollinate back to FORM snapshot on first bootstrap
      const formSnap = ensureBindingIds(parseMaybe(getFormSnapshot()));
      const updatedFormSnap = ensureMirrorsAndSyncProps(
        normalizedObj,
        formSnap
      );
      if (updatedFormSnap) {
        const s = safeStringify(updatedFormSnap);
        setFormSnapshot(s);
        dispatch(setFormLayout(s));
      }

      dispatch(setOutputLayout(normalizedStr));
      setReportSnapshot(normalizedStr);
      lastLoadedSerializedRef.current = normalizedStr;
    }
  }, [
    serialized,
    selected,
    shouldBootstrapForm,
    shouldBootstrapOutput,
    dispatch,
    getFormSnapshot,
    getReportSnapshot,
    setFormSnapshot,
    setReportSnapshot,
  ]);

  // Normal live-saving after first paint + create mirrors & sync whitelisted props
  React.useEffect(() => {
    if (!serialized?.includes('"ROOT"')) return;
    if (switchingRef.current) return;
    if (loadedBranchRef.current !== selected) return;

    // Avoid treating the bootstrap frame as an edit
    if (
      lastLoadedSerializedRef.current &&
      serialized === lastLoadedSerializedRef.current
    ) {
      lastLoadedSerializedRef.current = null;
      return;
    }

    const normalizedObj = ensureBindingIds(safeParse(serialized));
    const normalizedStr = safeStringify(normalizedObj);

    if (selected === 'FORM') {
      // Save FORM
      dispatch(setFormLayout(normalizedStr));
      setFormSnapshot(normalizedStr);
      // Create/Sync counterpart in REPORT (label/placeholder only; positions only on create)
      const reportSnap = ensureBindingIds(parseMaybe(getReportSnapshot()));
      const updatedReport = ensureMirrorsAndSyncProps(
        normalizedObj,
        reportSnap
      );
      if (updatedReport) {
        const s = safeStringify(updatedReport);
        setReportSnapshot(s);
        dispatch(setOutputLayout(s));
      }
    } else {
      // Save REPORT
      dispatch(setOutputLayout(normalizedStr));
      setReportSnapshot(normalizedStr);
      // Create/Sync counterpart in FORM
      const formSnap = ensureBindingIds(parseMaybe(getFormSnapshot()));
      const updatedForm = ensureMirrorsAndSyncProps(normalizedObj, formSnap);
      if (updatedForm) {
        const s = safeStringify(updatedForm);
        setFormSnapshot(s);
        dispatch(setFormLayout(s));
      }
    }
  }, [
    serialized,
    selected,
    dispatch,
    getFormSnapshot,
    getReportSnapshot,
    setFormSnapshot,
    setReportSnapshot,
  ]);

  return null;
};
/** =============================== /CraftSync =============================== */

const Builder: React.FC = () => {
  const [showBuilder, setShowBuilder] = useState(false);
  const selected = useSelector(selectFormView); // 'FORM' | 'REPORT'
  const formLayout = useSelector(selectFormLayout); // string | null
  const outputLayout = useSelector(selectOutputLayout); // string | null
  const { sitePermissions } = useSelector((state: any) => state.user);
  const EMPTY_TREE = makeEmptyTree();

  // In-memory authoritative snapshots for this session.
  // We read from Redux only on first seed; afterwards we update these directly.
  const formSnapshotRef = React.useRef<string | SerializedNodes | null>(null);
  const reportSnapshotRef = React.useRef<string | SerializedNodes | null>(null);

  // Helpers to update/get snapshot refs from CraftSync
  const setFormSnapshot = React.useCallback((s: string | SerializedNodes) => {
    formSnapshotRef.current = s;
  }, []);
  const setReportSnapshot = React.useCallback((s: string | SerializedNodes) => {
    reportSnapshotRef.current = s;
  }, []);
  const getFormSnapshot = React.useCallback(() => formSnapshotRef.current, []);
  const getReportSnapshot = React.useCallback(
    () => reportSnapshotRef.current,
    []
  );

  // On first mount or first visit to a branch, seed snapshotRefs from Redux/empty
  React.useEffect(() => {
    if (formSnapshotRef.current == null) {
      const seeded = ensureBindingIds(safeParse(formLayout)) || EMPTY_TREE;
      formSnapshotRef.current = safeStringify(seeded);
    }
    if (reportSnapshotRef.current == null) {
      const base =
        ensureBindingIds(safeParse(outputLayout)) ||
        ensureBindingIds(safeParse(formLayout)) ||
        EMPTY_TREE;
      reportSnapshotRef.current = safeStringify(base);
    }
  }, [selected, formLayout, outputLayout, EMPTY_TREE]);

  // --- Bootstrap conditions (consider empty if no children) ---
  const shouldBootstrapForm = selected === 'FORM' && isEmptyTree(formLayout);

  const shouldBootstrapOutput =
    selected === 'REPORT' &&
    isEmptyTree(outputLayout) &&
    !isEmptyTree(formLayout);

  // --- Frozen initialJson passed to CraftSync from authoritative snapshotRefs ---
  //eslint-disable-next-line
  const initialJson = useMemo(() => {
    if (selected === 'FORM') return formSnapshotRef.current ?? EMPTY_TREE;
    return reportSnapshotRef.current ?? EMPTY_TREE;
  }, [selected]);

  return (
    <div>
      {showBuilder ? (
        <div className={styles.headlineContainer}>
          <FaArrowLeftLong onClick={() => setShowBuilder(false)} />
          <p className={styles.headline}>Report Builder</p>
        </div>
      ) : (
        <div className={styles.headlineContainer}>
          {sitePermissions.createLayouts && (
            <button
              className={styles.button}
              onClick={() => setShowBuilder(true)}
            >
              New Layout
            </button>
          )}
          <p className={styles.headline}>Report Layouts</p>
        </div>
      )}
      {showBuilder ? (
        <BuilderModeContext.Provider value={selected}>
          <InteractionModeProvider value="BUILD">
            <Editor
              key={`editor-${selected}`} // new editor instance per branch
              resolver={{
                SnapCanvas,
                InputField,
                CheckboxField,
                MultilineTextbox,
                UploadArea,
                DropdownField,
                DatePickerField,
                TimePickerField,
              }}
              enabled
            >
              <CraftSync
                initialJson={initialJson}
                selected={selected}
                getFormSnapshot={getFormSnapshot}
                setFormSnapshot={setFormSnapshot}
                getReportSnapshot={getReportSnapshot}
                setReportSnapshot={setReportSnapshot}
                formLayoutRedux={formLayout}
                outputLayoutRedux={outputLayout}
                shouldBootstrapForm={shouldBootstrapForm}
                shouldBootstrapOutput={shouldBootstrapOutput}
              />

              <div className={styles.builderWrapper}>
                <BuilderToolbox setNew={setShowBuilder} />
                <div className={styles.canvas}>
                  {/* Tree is owned by deserialize; keep Frame empty */}
                  <Frame key={`frame-${selected}`} />
                </div>
              </div>
            </Editor>
          </InteractionModeProvider>
        </BuilderModeContext.Provider>
      ) : (
        <ExistingLayouts />
      )}
    </div>
  );
};

export default Builder;
